import { z } from "zod";
import {
  FULLSCREEN_OVERLAY_MODES,
  isTableauSubMode,
  isTransientOverlayMode,
  TITRE_OVERLAY_MODES,
} from "./overlay.js";
import type { ProjectState } from "./replicants.js";

/**
 * Air rules shared by the extension (which applies them) and the panels (which
 * preview them). Every timed banner chain and every "what comes back" decision
 * is made here, from the replicant — never from one operator's browser memory.
 */

export const airTargetSchema = z.object({ mode: z.string(), data: z.unknown().optional() });
export const airStepSchema = z.object({
  mode: z.string().min(1),
  data: z.unknown().optional(),
  holdMs: z.number().int().positive(),
});
export const airSequenceSchema = z.object({
  id: z.string(),
  key: z.string().nullable(),
  steps: z.array(airStepSchema).min(1),
  index: z.number().int().nonnegative(),
  stepEndsAt: z.number(),
  returnTo: airTargetSchema.nullable(),
});

export type AirTarget = z.infer<typeof airTargetSchema>;
export type AirStep = z.infer<typeof airStepSchema>;
export type AirSequence = z.infer<typeof airSequenceSchema>;
export type Overlay = ProjectState["overlay"];

const isFull = (m: string | null | undefined) => !!m && FULLSCREEN_OVERLAY_MODES.has(m);
const isTitre = (m: string | null | undefined) => !!m && TITRE_OVERLAY_MODES.has(m);
const isAutoLittle = (m: string) =>
  m === "little_sponsor" || m === "little_sponsor_2" || m === "little_extra_time";
const isBar = (m: string) => m === "scoreboard" || isTableauSubMode(m);

export function emptyOverlay(): Overlay {
  return {
    mode: null,
    data: undefined,
    shownAt: null,
    returnTo: null,
    scoreboardUnder: false,
    sequence: null,
  };
}

/** Is the scoreboard visible under what is on air (or behind the fullscreen)? */
export function scoreboardUnder(o: Overlay): boolean {
  const m = o.mode;
  if (!m) return false;
  if (isBar(m)) return true;
  if (isTitre(m)) return !!o.scoreboardUnder;
  if (isFull(m)) {
    const back = o.returnTo?.mode;
    return !!back && (isBar(back) || (isTitre(back) && !!o.scoreboardUnder));
  }
  return false;
}

/** What to put back later; null = the air was empty. */
export function captureReturn(o: Overlay): AirTarget | null {
  if (!o.mode) return null;
  if (isTransientOverlayMode(o.mode) || isAutoLittle(o.mode))
    return { mode: "scoreboard", data: {} };
  return { mode: o.mode, data: o.data };
}

/** What stays on air once `mode` is turned off; null = empty air. A fullscreen
 *  board never brings anything back — the operator decides what goes next. */
export function offTarget(o: Overlay, mode: string): AirTarget | null {
  if (isFull(mode)) return null;
  if (mode !== "scoreboard" && scoreboardUnder({ ...o, mode })) {
    return { mode: "scoreboard", data: {} };
  }
  return null;
}

export function overlayAfterShow(o: Overlay, mode: string, data: unknown, now: number): Overlay {
  let returnTo = o.returnTo ?? null;
  if (!isFull(mode)) returnTo = null;
  else if (!isFull(o.mode)) returnTo = captureReturn(o);
  const under = isTitre(mode) && !isTitre(o.mode) ? scoreboardUnder(o) : !!o.scoreboardUnder;
  return { mode, data, shownAt: now, returnTo, scoreboardUnder: under, sequence: null };
}

export function finishSequence(o: Overlay, now: number): Overlay {
  const back = o.sequence?.returnTo ?? null;
  return {
    ...o,
    mode: back?.mode ?? null,
    data: back?.data,
    shownAt: back ? now : null,
    sequence: null,
  };
}

export function overlayAfterOff(o: Overlay, mode: string, now: number): Overlay | null {
  if (o.mode !== mode) return null;
  const seq = o.sequence;
  if (seq && seq.steps[seq.index]?.mode === mode) return finishSequence(o, now);
  const t = offTarget(o, mode);
  return {
    mode: t?.mode ?? null,
    data: t?.data,
    shownAt: t ? now : null,
    returnTo: null,
    scoreboardUnder: false,
    sequence: null,
  };
}

export function startSequence(
  o: Overlay,
  steps: AirStep[],
  key: string | null,
  id: string,
  now: number,
): Overlay {
  const first = steps[0];
  if (!first) return o;
  const running = o.sequence;
  if (running && key !== null && running.key === key && o.mode === first.mode) {
    return { ...o, sequence: { ...running, index: 0, stepEndsAt: now + first.holdMs } };
  }
  return {
    ...o,
    mode: first.mode,
    data: first.data,
    shownAt: now,
    sequence: {
      id,
      key,
      steps,
      index: 0,
      stepEndsAt: now + first.holdMs,
      returnTo: running ? running.returnTo : captureReturn(o),
    },
  };
}

export function tickSequence(o: Overlay, now: number): Overlay | null {
  const seq = o.sequence;
  if (!seq || now < seq.stepEndsAt) return null;
  if (o.mode !== seq.steps[seq.index]?.mode) return { ...o, sequence: null };
  const next = seq.steps[seq.index + 1];
  if (!next) return finishSequence(o, now);
  return {
    ...o,
    mode: next.mode,
    data: next.data,
    shownAt: now,
    sequence: { ...seq, index: seq.index + 1, stepEndsAt: now + next.holdMs },
  };
}
